/**
 * Sidebar navigation matching Raiqon design
 */

import React, { useState } from 'react';
import { ActivityIcon } from './icons';
import './Sidebar.css';

interface SidebarProps {
  currentView: 'list' | 'builder' | 'executions' | 'execution-monitor' | 'agents' | 'settings';
  onViewChange: (view: 'list' | 'builder' | 'executions' | 'execution-monitor' | 'agents' | 'settings') => void;
  onLogout: () => void;
}


export const Sidebar: React.FC<SidebarProps> = ({ currentView, onViewChange, onLogout }) => {
  const [isExpanded] = useState(true); // Always expanded

  return (
    <div className={`sidebar ${isExpanded ? 'expanded' : ''}`}>

      <div className="sidebar-header">
        <div className="logo">
          <img src={`${import.meta.env.BASE_URL}raiqon_logo.png`} alt="RAIQON Logo" className="logo-icon" />
          <span className="logo-text">RAIQON</span>
        </div>
      </div>

      <div className="sidebar-section">
        <button
          className={`sidebar-item ${currentView === 'list' || currentView === 'builder' ? 'active' : ''}`}
          onClick={() => onViewChange('list')}
        >
          <svg width="16" height="16" viewBox="0 -960 960 960" fill="currentColor">
            <path d="M200-80q-50 0-85-35t-35-85q0-39 22.5-70t57.5-43v-87q0-50 35-85t85-35h160v-127q-35-12-57.5-43T360-760q0-50 35-85t85-35q50 0 85 35t35 85q0 39-22.5 70T520-647v127h160q50 0 85 35t35 85v87q35 12 57.5 43t22.5 70q0 50-35 85t-85 35q-50 0-85-35t-35-85q0-39 22.5-70t57.5-43v-87q0-17-11.5-28.5T680-440H520v127q35 12 57.5 43t22.5 70q0 50-35 85t-85 35q-50 0-85-35t-35-85q0-39 22.5-70t57.5-43v-127H280q-17 0-28.5 11.5T240-400v87q35 12 57.5 43t22.5 70q0 50-35 85t-85 35Zm0-80q17 0 28.5-11.5T240-200q0-17-11.5-28.5T200-240q-17 0-28.5 11.5T160-200q0 17 11.5 28.5T200-160Zm280 0q17 0 28.5-11.5T520-200q0-17-11.5-28.5T480-240q-17 0-28.5 11.5T440-200q0 17 11.5 28.5T480-160Zm280 0q17 0 28.5-11.5T800-200q0-17-11.5-28.5T760-240q-17 0-28.5 11.5T720-200q0 17 11.5 28.5T760-160ZM480-720q17 0 28.5-11.5T520-760q0-17-11.5-28.5T480-800q-17 0-28.5 11.5T440-760q0 17 11.5 28.5T480-720Z" />
          </svg>
          <span>Workflows</span>
        </button>
        <button
          className={`sidebar-item ${currentView === 'executions' || currentView === 'execution-monitor' ? 'active' : ''}`}
          onClick={() => onViewChange('executions')}
        >
          <ActivityIcon size={16} />
          <span>Executions</span>
        </button>
        <button
          className={`sidebar-item ${currentView === 'agents' ? 'active' : ''}`}
          onClick={() => onViewChange('agents')}
        >
          <svg width="16" height="16" viewBox="0 -960 960 960" fill="currentColor">
            <path d="M160-360q-50 0-85-35t-35-85q0-50 35-85t85-35v-80q0-33 23.5-56.5T240-760h120q0-50 35-85t85-35q50 0 85 35t35 85h120q33 0 56.5 23.5T800-680v80q50 0 85 35t35 85q0 50-35 85t-85 35v160q0 33-23.5 56.5T720-120H240q-33 0-56.5-23.5T160-200v-160Zm242.5-97.5Q420-475 420-500t-17.5-42.5Q385-560 360-560t-42.5 17.5Q300-525 300-500t17.5 42.5Q335-440 360-440t42.5-17.5Zm240 0Q660-475 660-500t-17.5-42.5Q625-560 600-560t-42.5 17.5Q540-525 540-500t17.5 42.5Q575-440 600-440t42.5-17.5ZM320-280h320v-80H320v80Zm-80 80h480v-480H240v480Zm240-240Z" />
          </svg>
          <span>Agents</span>
        </button>
      </div>

      <div className="sidebar-footer">
        <button
          className={`sidebar-item ${currentView === 'settings' ? 'active' : ''}`}
          onClick={() => onViewChange('settings')}
        >
          <svg width="16" height="16" viewBox="0 -960 960 960" fill="currentColor">
            <path d="m370-80-16-128q-13-5-24.5-12T307-235l-119 50L78-375l103-78q-1-7-1-13.5v-27q0-6.5 1-13.5L78-585l110-190 119 50q11-8 23-15t24-12l16-128h220l16 128q13 5 24.5 12t22.5 15l119-50 110 190-103 78q1 7 1 13.5v27q0 6.5-2 13.5l103 78-110 190-118-50q-11 8-23 15t-24 12L590-80H370Zm70-80h79l14-106q31-8 57.5-23.5T639-327l99 41 39-68-86-65q5-14 7-29.5t2-31.5q0-16-2-31.5t-7-29.5l86-65-39-68-99 42q-22-23-48.5-38.5T533-694l-13-106h-79l-14 106q-31 8-57.5 23.5T321-633l-99-41-39 68 86 64q-5 15-7 30t-2 32q0 16 2 31t7 30l-86 65 39 68 99-42q22 23 48.5 38.5T427-266l13 106Zm42-180q58 0 99-41t41-99q0-58-41-99t-99-41q-59 0-99.5 41T342-480q0 58 40.5 99t99.5 41Zm-2-140Z" />
          </svg>
          <span>Settings</span>
        </button>
        <button className="sidebar-item sidebar-link" onClick={onLogout}>
          <span className="text-xs text-secondary">Log out</span>
        </button>
        <a
          href="https://raiqon.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="sidebar-item sidebar-link"
        >
          <span className="text-xs text-secondary">Powered by Raiqon AI</span>
        </a>
      </div>
    </div>
  );
};
